import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiCheckCircle, FiClock, FiDollarSign, FiDownload, FiPlus, FiUsers } from "react-icons/fi";
import { formatNaira } from "../../../utils/dashboardFormat";
import { compactNaira } from "../../../components/MemberProfile/profileModel";
import {
  useGetContributionCategoryContributorsQuery,
  useGetMembersContributionHistoryV2Query,
} from "../../../store/admin/contribution";
import {
  KIND_META, STATUS_META, contributorFilters, dateRange, normalizeContributors, normalizePayments, whenLabel,
} from "../../../components/Contributions/contribModel";
import { downloadContributionReport } from "../../../components/Contributions/exportReport";
import { useDebounced } from "../../../components/Contributions/hooks";
import PaymentsTable from "../../../components/Contributions/PaymentsTable";
import { Chips, Notice, PageHeader, Pager, Person, Progress, SearchBox, Skeleton, StatusPill, Tabs, Tile } from "../../../components/Contributions/parts";

const OVERVIEW = "/super_admin/contribution/contribution_history";
const RANGES = [
  { key: "all", label: "Any date" },
  { key: "month", label: "This month" },
  { key: "week", label: "This week" },
  { key: "today", label: "Today" },
];

/**
 * One category: who has paid what (contributors) and every payment made into it.
 */
export default function CategoryDetail() {
  const { id } = useParams();
  const [tab, setTab] = useState("contributors");

  // contributors
  const [status, setStatus] = useState("has_paid");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const q = useDebounced(search);
  useEffect(() => setPage(1), [status, q, id]);

  const contributors = useGetContributionCategoryContributorsQuery({ categoryId: id, page, status, search: q });
  const data = useMemo(() => (contributors.data ? normalizeContributors(contributors.data) : null), [contributors.data]);
  const category = data?.category || null;

  // payments
  const [pSearch, setPSearch] = useState("");
  const [range, setRange] = useState("all");
  const [pPage, setPPage] = useState(1);
  const pq = useDebounced(pSearch);
  const { from, to } = dateRange(range);
  useEffect(() => setPPage(1), [pq, range, id]);

  const payments = useGetMembersContributionHistoryV2Query(
    { contribution_category_id: id, page: pPage, search: pq, from_date: from, to_date: to },
    { skip: tab !== "payments" }
  );
  const paymentData = useMemo(() => normalizePayments(payments.data), [payments.data]);

  const [exporting, setExporting] = useState(false);
  const exportAs = async (format) => {
    setExporting(true);
    try {
      await downloadContributionReport(format, { contribution_category_id: id, search: pq, from_date: from, to_date: to });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  const notFound = contributors.isError && contributors.error?.status === 404;
  const failed = contributors.isError && !notFound;

  if (notFound) {
    return (
      <section className="cb">
        <PageHeader title="Category not found" backTo={OVERVIEW} backLabel="Totals by category" />
        <div className="cb-card">
          <Notice title="This category isn't in your branch" actionLabel="Back to totals" actionTo={OVERVIEW}>
            It may belong to another branch, or it may have been removed.
          </Notice>
        </div>
      </section>
    );
  }

  const perMember = category?.perMember;
  const tone = category ? KIND_META[category.kind].tone : "primary";
  const subtitle = category
    ? [
        KIND_META[category.kind].label,
        perMember ? `${formatNaira(perMember)} per member` : null,
        category.target ? `${formatNaira(category.target)} target` : null,
        category.endDate && whenLabel(category.endDate) ? `ends ${whenLabel(category.endDate)}` : null,
      ].filter(Boolean).join(" · ")
    : "";

  return (
    <section className="cb" aria-label={category ? `${category.name} contributions` : "Category contributions"}>
      <PageHeader
        title={category ? category.name : "Category"}
        sub={subtitle}
        backTo={OVERVIEW}
        backLabel="Totals by category"
        actions={
          <Link to={`/super_admin/contribution/record_contribution/${id}`} className="cb-btn primary">
            <FiPlus size={16} aria-hidden="true" /> Record payment
          </Link>
        }
      />

      {category && data ? (
        <div className="cb-tiles" role="group" aria-label="Summary">
          <Tile icon={FiDollarSign} tone="green" label="Collected" value={compactNaira(category.collected)} />
          {perMember ? (
            <>
              <Tile icon={FiCheckCircle} tone="green" label="Paid up" value={data.counts.paidUp} sub={`of ${data.counts.hasPaid + data.counts.notStarted}`} quiet={!data.counts.paidUp} />
              <Tile icon={FiClock} tone="amber" label="Part paid" value={data.counts.partPaid} quiet={!data.counts.partPaid} />
              <Tile icon={FiUsers} tone="grey" label="Not started" value={data.counts.notStarted} quiet={!data.counts.notStarted} />
            </>
          ) : (
            <>
              <Tile icon={FiUsers} tone="purple" label="Contributors" value={data.counts.hasPaid} quiet={!data.counts.hasPaid} />
              <Tile icon={FiClock} tone="blue" label="This month" value={compactNaira(category.thisMonth)} quiet={!category.thisMonth} />
              <Tile icon={FiCheckCircle} tone="grey" label="Payments" value={category.paymentsCount} quiet={!category.paymentsCount} />
            </>
          )}
        </div>
      ) : null}

      <div className="cb-card">
        <Tabs
          label="Category sections"
          active={tab}
          onChange={setTab}
          tabs={[
            { key: "contributors", label: "Contributors", count: data ? data.counts.hasPaid : undefined },
            { key: "payments", label: "Payments", count: category ? category.paymentsCount : undefined },
          ]}
        />

        {tab === "contributors" ? (
          <div role="tabpanel" aria-label="Contributors">
            <div className="cb-toolbar">
              <SearchBox value={search} onChange={setSearch} placeholder="Search by name or member number" label="Search contributors" />
            </div>
            {data ? (
              <div style={{ marginBottom: 12 }}>
                <Chips items={contributorFilters(category, data.counts)} active={status} onChange={setStatus} label="Show members" />
              </div>
            ) : null}

            {contributors.isLoading ? <Skeleton rows={5} /> : null}
            {failed ? (
              <Notice tone="amber" title="Couldn't load the contributors" actionLabel="Try again" onAction={() => contributors.refetch()}>
                The category is fine; we just couldn't fetch who has paid. Try again in a moment.
              </Notice>
            ) : null}

            {data && !contributors.isLoading ? (
              data.members.length === 0 ? (
                <Notice
                  title={status === "has_paid" && !q ? "No payments yet" : "No members match"}
                  actionLabel={status === "has_paid" && !q ? "Record the first payment" : undefined}
                  actionTo={status === "has_paid" && !q ? `/super_admin/contribution/record_contribution/${id}` : undefined}
                >
                  {status === "has_paid" && !q ? "Nobody has paid into this category in this branch." : "Try another filter or search."}
                </Notice>
              ) : (
                <>
                  <div className="cb-scroll">
                    <table className="cb-table">
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th className="num">Paid</th>
                          {perMember ? <th>Progress</th> : null}
                          <th>Status</th>
                          <th>Last payment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.members.map((m) => (
                          <tr key={m.id}>
                            <td>
                              <Link to={`/super_admin/member_management/members/${m.id}`} className="cb-rowlink" aria-label={`Open ${m.name}'s profile`}>
                                <Person name={m.name} sub={m.memberNo ? `#${m.memberNo}` : ""} photo={m.photo} />
                              </Link>
                            </td>
                            <td className="num" style={{ fontWeight: 600, color: "var(--cb-ink)" }}>{formatNaira(m.paid)}</td>
                            {perMember ? (
                              <td style={{ minWidth: 130 }}>
                                <span className="cb-faint">{m.remaining ? `${formatNaira(m.remaining)} to go` : "Complete"}</span>
                                <Progress value={Math.min(100, (m.paid / perMember) * 100)} tone={STATUS_META[m.status]?.tone || tone} label={`${m.name} progress`} />
                              </td>
                            ) : null}
                            <td>
                              <StatusPill status={m.status} />
                            </td>
                            <td className="cb-mute">{whenLabel(m.lastPaymentAt) || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager page={data.pagination.page} pages={data.pagination.pages} total={data.pagination.total} perPage={data.pagination.perPage} noun="members" onPage={setPage} />
                </>
              )
            ) : null}
          </div>
        ) : (
          <div role="tabpanel" aria-label="Payments">
            <div className="cb-toolbar">
              <SearchBox value={pSearch} onChange={setPSearch} placeholder="Search by reference or amount" label="Search payments" />
              <select className="cb-field" aria-label="Date range" value={range} onChange={(e) => setRange(e.target.value)}>
                {RANGES.map((r) => (
                  <option key={r.key} value={r.key}>{r.label}</option>
                ))}
              </select>
              <button type="button" className="cb-btn" disabled={exporting} onClick={() => exportAs("excel")}>
                <FiDownload size={16} aria-hidden="true" /> {exporting ? "Preparing..." : "Export CSV"}
              </button>
              <button type="button" className="cb-btn" disabled={exporting} onClick={() => exportAs("pdf")}>
                Printable
              </button>
            </div>

            {paymentData.summary.known ? (
              <p className="cb-mute" style={{ marginBottom: 10 }}>
                {paymentData.summary.count} {paymentData.summary.count === 1 ? "payment" : "payments"}, {formatNaira(paymentData.summary.total)} received
                {paymentData.summary.pending ? `, ${paymentData.summary.pending} waiting for confirmation` : ""}
              </p>
            ) : null}

            {payments.isLoading || payments.isFetching ? <Skeleton rows={5} /> : null}
            {payments.isError ? (
              <Notice tone="amber" title="Couldn't load the payments" actionLabel="Try again" onAction={() => payments.refetch()}>
                Try again in a moment.
              </Notice>
            ) : null}
            {!payments.isLoading && !payments.isFetching && !payments.isError ? (
              paymentData.rows.length === 0 ? (
                <Notice title="No payments found">{pq || range !== "all" ? "Try a different search or date range." : "Payments made into this category will show up here."}</Notice>
              ) : (
                <>
                  <PaymentsTable rows={paymentData.rows} showCategory={false} />
                  <Pager page={paymentData.pagination.page} pages={paymentData.pagination.pages} total={paymentData.pagination.total} perPage={paymentData.pagination.perPage} noun="payments" onPage={setPPage} />
                </>
              )
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
